import type { CheckinKind, ShiftState } from './constants';
import { zonedNow } from './live';
import { minutesToHm } from './time';

/**
 * "Ne pride" — which of a withdrawing volunteer's shifts are freed (SPEC §1.1K).
 *
 * Freed means the slot goes back to PROSTO with its time and meeting point, so a
 * post never looks covered by somebody who is not coming. What stays with the
 * person is what they have already given or are giving: a shift that is over,
 * and any shift somebody checked them in to. A shift that has started with
 * nobody checked in is freed as well — the post is empty right now, and that is
 * the case race morning produces.
 *
 * The decision reads the clock and the check-ins, never `state === 'done'`: no
 * code writes that state, so a worked shift is still `assigned`. Days and minutes
 * are Europe/Ljubljana, the way every schedule time in this app is stored.
 */

export interface CancelPlanShift {
  id: string;
  postId: string;
  state: ShiftState;
  /** `event_day.key` of the shift's own day. */
  dayKey: string;
  fromMin: number;
  toMin: number;
}

export interface CancelPlanCheckin {
  shiftId: string;
  kind: CheckinKind;
  /** Minutes from midnight of the day it was recorded on — it starts over at 00:00. */
  atMin: number;
  /**
   * When it was recorded. What orders a shift's check-ins across midnight,
   * where `atMin` cannot; give it for all of them or for none.
   */
  at?: Date | string;
}

export interface CancelPlanInput {
  /** Every shift the person holds, whatever its state. */
  shifts: CancelPlanShift[];
  checkins: CancelPlanCheckin[];
  /** The event's days, with their ISO dates (YYYY-MM-DD). */
  days: { key: string; date: string }[];
  now: Date;
}

export type KeepReason =
  | { kind: 'past' }
  | {
      kind: 'checked-in';
      /** First check-in, in minutes from midnight of the day it was recorded on. */
      inMin: number;
      /** Set when the person checked out again and has not checked back in. */
      outMin: number | null;
      running: boolean;
    };

export interface CancelPlan {
  /** Shifts to free. `running` marks a post that is empty right now. */
  free: { shiftId: string; running: boolean }[];
  keep: { shiftId: string; reason: KeepReason }[];
}

interface Presence {
  inMin: number;
  outMin: number | null;
}

/**
 * Whether these check-ins put somebody at the post, and whether they have since
 * left: present from the first `in`, gone when the last word is an `out`.
 *
 * Ordered by when each was recorded, because `atMin` starts over at midnight —
 * a check-out at 00:30 would otherwise sort before the check-in at 22:00 and
 * leave them standing there. A row without an instant falls back to its minute,
 * `dayOffset` minutes on for a piece recorded on the following day.
 */
function presence(rows: { c: CancelPlanCheckin; dayOffset: number }[]): Presence | null {
  const key = ({ c, dayOffset }: { c: CancelPlanCheckin; dayOffset: number }) =>
    c.at !== undefined ? new Date(c.at).getTime() : dayOffset + c.atMin;
  const mine = [...rows].sort((a, b) => key(a) - key(b)).map((r) => r.c);
  const firstIn = mine.find((c) => c.kind === 'in');
  if (!firstIn) return null;
  const last = mine[mine.length - 1]!;
  return { inMin: firstIn.atMin, outMin: last.kind === 'out' ? last.atMin : null };
}

const rowsOf = (checkins: CancelPlanCheckin[], shiftId: string, dayOffset = 0) =>
  checkins.filter((c) => c.shiftId === shiftId).map((c) => ({ c, dayOffset }));

/** Whether a shift was checked in to, and whether the person has since left. */
function presenceOn(checkins: CancelPlanCheckin[], shiftId: string): Presence | null {
  return presence(rowsOf(checkins, shiftId));
}

export function cancelPlan({ shifts, checkins, days, now }: CancelPlanInput): CancelPlan {
  const today = zonedNow(now);
  const dateOf = new Map(days.map((d) => [d.key, d.date]));
  const dayOrder = [...days].sort((a, b) => a.date.localeCompare(b.date)).map((d) => d.key);
  const dayIndex = (key: string) => {
    const i = dayOrder.indexOf(key);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  /**
   * The import splits a shift that runs past midnight into `[od, 24:00]` on its
   * day and `[00:00, do]` on the next (IMPORT-2026.md), and a check-in lands on
   * the first piece only. So the second piece counts as checked in while the
   * person is still standing at the post from the first one — otherwise it would
   * open the post under them at 23:00, or again at 00:30. A check-out ends it
   * on either piece, the one recorded after midnight included.
   */
  const carriedOver = (s: CancelPlanShift): Presence | null => {
    if (s.fromMin !== 0) return null;
    const prevKey = dayOrder[dayOrder.indexOf(s.dayKey) - 1];
    if (prevKey === undefined) return null;
    const prev = shifts.find(
      (p) => p.postId === s.postId && p.dayKey === prevKey && p.toMin === 1440 && p.state !== 'cancelled',
    );
    const there = prev ? presence([...rowsOf(checkins, prev.id), ...rowsOf(checkins, s.id, 1440)]) : null;
    return there && there.outMin === null ? there : null;
  };

  const plan: CancelPlan = { free: [], keep: [] };
  const ordered = shifts
    .filter((s) => s.state !== 'cancelled')
    .sort((a, b) => dayIndex(a.dayKey) - dayIndex(b.dayKey) || a.fromMin - b.fromMin);

  for (const s of ordered) {
    const date = dateOf.get(s.dayKey);
    // A day the event does not know is never a reason to free anything.
    if (date === undefined) {
      plan.keep.push({ shiftId: s.id, reason: { kind: 'past' } });
      continue;
    }
    const isToday = date === today.isoDate;
    const over = s.state === 'done' || date < today.isoDate || (isToday && s.toMin <= today.minutes);
    if (over) {
      plan.keep.push({ shiftId: s.id, reason: { kind: 'past' } });
      continue;
    }
    const running = isToday && s.fromMin <= today.minutes;
    const here = presenceOn(checkins, s.id) ?? carriedOver(s);
    if (here) {
      plan.keep.push({ shiftId: s.id, reason: { kind: 'checked-in', ...here, running } });
      continue;
    }
    plan.free.push({ shiftId: s.id, running });
  }
  return plan;
}

/** How the dialog and the Ne pride card name a shift that stays with the person. */
export function keepReasonLabel(r: KeepReason): string {
  if (r.kind === 'past') return 'že mimo';
  const out = r.outMin !== null ? ` · odjava ${minutesToHm(r.outMin)}` : '';
  return `${r.running ? 'teče · ' : ''}prijava ${minutesToHm(r.inMin)}${out}`;
}

/**
 * "izmena" in the accusative with its number, as a button needs it:
 * "Sprosti izmeno", "Sprosti 2 izmeni", "Sprosti 3 izmene", "Sprosti 5 izmen".
 */
export function izmeneAcc(n: number): string {
  const m = n % 100;
  const word = m === 1 ? 'izmeno' : m === 2 ? 'izmeni' : m === 3 || m === 4 ? 'izmene' : 'izmen';
  return n === 1 ? word : `${n} ${word}`;
}
